"use client";

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@workspace/ui/components/sidebar";
import {
  ArrowLeft,
  Blocks,
  BookOpen,
  Building2,
  CreditCard,
  FileText,
  FolderOpen,
  Landmark,
  type LucideIcon,
  MapPin,
  Package,
  ScanBarcode,
  Settings2,
  ShoppingCart,
  Users,
  Warehouse,
} from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import { cn } from "@workspace/ui/lib/utils";
import {
  hasFullPlanCapabilities,
  type OrganizationPlanType,
} from "@/features/organizations/utils";
import { canAccessConfigurationPath } from "@/lib/constants/permissions";
import type { NewSaleDefaultPath } from "@/lib/constants/new-sale-default-path";
import { NavMain } from "./nav-main";
import type { SidebarNavItem } from "./navigation-config";

type AppSidebarContentProps = {
  defaultNewSalePath: NewSaleDefaultPath;
  navItems: SidebarNavItem[];
  orgSlug: string;
  planType?: OrganizationPlanType;
  userPermissions: string[];
};

type ConfigurationSidebarItem = {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
};

const BASE_CONFIGURATION_ITEMS: ConfigurationSidebarItem[] = [
  {
    id: "organizacion",
    label: "Organización",
    path: "/configuracion",
    icon: Building2,
  },
  { id: "plan", label: "Plan", path: "/configuracion/plan", icon: CreditCard },
  {
    id: "ventas",
    label: "Ventas",
    path: "/configuracion/ventas",
    icon: ShoppingCart,
  },
  {
    id: "comprobantes",
    label: "Comprobantes",
    path: "/configuracion/comprobantes",
    icon: FileText,
  },
  {
    id: "productos",
    label: "Productos",
    path: "/configuracion/productos",
    icon: Package,
  },
  {
    id: "puntos-venta",
    label: "Puntos de Venta",
    path: "/configuracion/puntos-venta",
    icon: MapPin,
  },
  {
    id: "ubicaciones",
    label: "Ubicaciones",
    path: "/configuracion/ubicaciones",
    icon: Warehouse,
  },
  {
    id: "bancos",
    label: "Bancos",
    path: "/configuracion/bancos",
    icon: Landmark,
  },
  {
    id: "medios-pago",
    label: "Medios de Pago",
    path: "/configuracion/medios-pago",
    icon: CreditCard,
  },
  {
    id: "categorias",
    label: "Categorías",
    path: "/configuracion/categorias",
    icon: FolderOpen,
  },
  {
    id: "contabilidad",
    label: "Contabilidad",
    path: "/configuracion/contabilidad",
    icon: BookOpen,
  },
  {
    id: "colaboradores",
    label: "Colaboradores",
    path: "/configuracion/colaboradores",
    icon: Users,
  },
  {
    id: "integraciones",
    label: "Integraciones",
    path: "/configuracion/integraciones",
    icon: Blocks,
  },
];

const CUSTOM_FIELDS_ITEM: ConfigurationSidebarItem = {
  id: "campos-personalizados",
  label: "Campos personalizados",
  path: "/configuracion/campos-personalizados",
  icon: Settings2,
};

const PRICE_CHECKER_ITEM: ConfigurationSidebarItem = {
  id: "verificador-precios",
  label: "Verificador de precios",
  path: "/configuracion/verificador-precios",
  icon: ScanBarcode,
};

function getConfigurationItems(
  planType?: OrganizationPlanType,
  _orgSlug?: string
): ConfigurationSidebarItem[] {
  const baseItems = [...BASE_CONFIGURATION_ITEMS];

  if (hasFullPlanCapabilities(planType)) {
    const items = [...baseItems];
    const integrationsIndex = items.findIndex(
      (item) => item.id === "integraciones"
    );
    items.splice(integrationsIndex, 0, PRICE_CHECKER_ITEM);
    items.splice(integrationsIndex, 0, CUSTOM_FIELDS_ITEM);
    return items;
  }

  return baseItems;
}

function isConfigurationRoute(pathname: string, orgSlug: string): boolean {
  const basePath = `/${orgSlug}/configuracion`;
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

function isConfigItemActive(
  pathname: string,
  orgSlug: string,
  itemPath: string
): boolean {
  const fullPath = `/${orgSlug}${itemPath}`;
  if (itemPath === "/configuracion") {
    return pathname === fullPath || pathname === `${fullPath}/`;
  }

  return pathname === fullPath || pathname.startsWith(`${fullPath}/`);
}

function ConfigurationNavMain({
  items,
  orgSlug,
}: {
  items: ConfigurationSidebarItem[];
  orgSlug: string;
}) {
  const pathname = usePathname();
  const { setOpenMobile } = useSidebar();

  const handleNavigation = () => {
    setOpenMobile(false);
  };

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="py-6 text-lg text-muted-foreground md:py-0 md:text-sm [&_svg]:text-foreground/70 hover:[&_svg]:text-foreground"
              render={<Link href={`/${orgSlug}`} onClick={handleNavigation} />}
              tooltip="Volver"
            >
              <ArrowLeft />
              <span>Volver</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = isConfigItemActive(pathname, orgSlug, item.path);
            return (
              <SidebarMenuItem key={item.id}>
                <SidebarMenuButton
                  className={cn(
                    "py-6 text-lg md:py-0 md:text-sm [&_svg]:text-foreground/70 hover:[&_svg]:text-foreground",
                    isActive &&
                      "bg-sidebar-accent font-semibold text-sidebar-accent-foreground"
                  )}
                  isActive={isActive}
                  render={
                    <Link
                      aria-current={isActive ? "page" : undefined}
                      href={`/${orgSlug}${item.path}`}
                      onClick={handleNavigation}
                    />
                  }
                  tooltip={item.label}
                >
                  <Icon />
                  <span>{item.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

export function AppSidebarContent({
  defaultNewSalePath,
  navItems,
  orgSlug,
  planType,
  userPermissions,
}: AppSidebarContentProps) {
  const pathname = usePathname();
  const { isMobile, open, setOpen } = useSidebar();
  const isConfigRoute = isConfigurationRoute(pathname, orgSlug);

  useEffect(() => {
    if (!isMobile && isConfigRoute && !open) {
      setOpen(true);
    }
  }, [isMobile, isConfigRoute, open, setOpen]);

  if (!isConfigRoute) {
    return (
      <NavMain
        defaultNewSalePath={defaultNewSalePath}
        items={navItems}
        userPermissions={userPermissions}
      />
    );
  }

  return (
    <ConfigurationNavMain
      items={getConfigurationItems(planType, orgSlug).filter((item) =>
        canAccessConfigurationPath(userPermissions, item.path)
      )}
      orgSlug={orgSlug}
    />
  );
}
